'use client';

import { FC, useEffect, useState } from 'react';

import { getShoppingCartUrls } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { Price, ShoppingCartUrls } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItem } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { getLanguageCode } from '../../../../../utils/locale/locale.utils';
import { SystemPageLink } from '../../../system-page-link/system-page-link';

import { CheckoutSummaryItemElement } from './checkout-summary-item-element';

interface Props {
   shoppingCartItems: Array<ShoppingCartItem>;
   locale: string;
}

export const CheckoutSummary: FC<Props> = ({ shoppingCartItems, locale }) => {
   const translate = useTranslations();
   const format = useFormatter();
   const [shoppingCartBaseUrls, setShoppingCartBaseUrls] = useState<ShoppingCartUrls>({
      shoppingCartBaseUrl: '',
      checkoutBaseUrl: ''
   });

   const totalPrice: Price = {
      amount: shoppingCartItems.reduce((acc, cartItem) => acc + cartItem.unitPrice.price.amount * cartItem.quantity.amount, 0),
      currency: shoppingCartItems.length > 0 ? shoppingCartItems[0].unitPrice.price.currency : ''
   };

   useEffect(() => {
      const fetchShoppingCartUrls = async () => {
         const orderBaseUrlsResponse = await getShoppingCartUrls(getLanguageCode(locale));
         setShoppingCartBaseUrls(orderBaseUrlsResponse);
      };
      fetchShoppingCartUrls();
   }, [locale]);

   return (
      <Paper>
         <Box direction="column" gap="sm">
            <Box justify="between" items="center">
               <Typography component="h4" variant="heading-4">
                  {translate('shopping-cart.checkout.cart-summary')}
               </Typography>
               <Box justify="end">
                  <SystemPageLink underline="none" baseRoute="my-shopping-cart" locale={locale} type={SystemPageType.SHOPPING_CART}>
                     <Button variant="secondary" leadingIcon="edit">
                        {translate('shopping-cart.checkout.edit')}
                     </Button>
                  </SystemPageLink>
               </Box>
            </Box>
            <Divider />

            {shoppingCartItems.map((shoppingCartItem) => (
               <CheckoutSummaryItemElement locale={locale} key={`${shoppingCartItem.id}`} shoppingCartItem={shoppingCartItem} />
            ))}
            <Box justify="between" items="baseline">
               <Typography>{translate('shopping-cart.checkout.total-price')}</Typography>
               <Typography variant="heading-4">{shoppingCartItems.length > 0 ? format.price(totalPrice) : ''}</Typography>
            </Box>
         </Box>
      </Paper>
   );
};
